// Subject 7: the phone reminders and the protocol cues as the admin set them
// (GET /notifications/config), merged over the defaults shipped with the app.
// Free of Capacitor and of the app's '@/' imports, so the unit tests run it under node.
//
// The config: { version, locale, quiet_hours: { start, end }, types: { [key]: { enabled, params, title, body } } },
// enabled once the person's categories and the admin's emergency switch are
// applied, title and body null where the app keeps its own text. Never synced
// (first launch, offline), every type keeps the app's defaults.

/** What a reminder may take from the admin's parameters, with the values the OS accepts. */
const REMINDER_PARAMS = {
    days: (value) => Number.isInteger(value) && value >= 1 && value <= 365,
    hour: (value) => Number.isInteger(value) && value >= 0 && value <= 23,
    minute: (value) => Number.isInteger(value) && value >= 0 && value <= 59,
}

/** One type of the config, or null when the config does not know it. */
export function phoneType(config, key) {
    const type = config?.types?.[key]
    return type && typeof type === 'object' ? type : null
}

const text = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null)

/**
 * One reminder of src/lib/local-notifications.js as the admin set it: on or
 * off, its days, hour and minute when valid, and the admin's title and body
 * (null: the app's own text). The shape (days or end of month) stays the app's.
 */
export function mergeReminder(reminder, config) {
    const type = phoneType(config, reminder.key)
    if (!type) return { ...reminder, title: null, body: null }

    const params = Object.fromEntries(
        Object.entries(REMINDER_PARAMS)
            .filter(([name, valid]) => name in reminder && valid(type.params?.[name]))
            .map(([name]) => [name, type.params[name]])
    )

    return {
        ...reminder,
        ...params,
        enabled: reminder.enabled && type.enabled !== false,
        title: text(type.title),
        body: text(type.body),
    }
}

/** An admin text with its {variables} filled; a variable not given stays as written. */
export function fillText(template, variables = {}) {
    if (!text(template)) return null
    return template.replace(/\{(\w+)\}/g, (match, name) => (name in variables ? String(variables[name]) : match))
}

/** The API's own defaults (NotificationSetting), for a config never synced or synced before it sent them. */
export const DEFAULT_QUIET_HOURS = Object.freeze({ start: '22:00', end: '07:00' })

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/

/** The quiet hours the API applies, HH:MM on the phone; unknown or unreadable, 22:00 to 07:00. */
export function quietHours(config) {
    const hours = config?.quiet_hours
    return CLOCK.test(hours?.start) && CLOCK.test(hours?.end)
        ? { start: hours.start, end: hours.end }
        : DEFAULT_QUIET_HOURS
}

/** One end of the quiet hours in the person's language: the locale files hold its am/pm and minutes forms. */
function quietTime(clock, t) {
    const [, hours, minute] = clock.match(CLOCK)
    const hour = Number(hours)
    const form = `${hour < 12 ? 'am' : 'pm'}${minute === '00' ? '' : '-minutes'}`
    return t(`notifications.settings.quiet-time.${form}`, { hour, hour12: hour % 12 || 12, minute })
}

/**
 * The settings page's sentence (« Rien entre 22 h et 7 h… »), t being vue-i18n's.
 * Null when the admin set the same start and end: the API then holds nothing back.
 */
export function quietHoursText(config, t) {
    const { start, end } = quietHours(config)
    return start === end
        ? null
        : t('notifications.settings.quiet', { start: quietTime(start, t), end: quietTime(end, t) })
}

const positive = (value, fallback) => (Number.isInteger(value) && value > 0 ? value : fallback)

/**
 * The protocol cues: on or off, the admin's texts, and the pre-alert's timings.
 * defaults: { seconds, minGaitSeconds } of src/lib/protocol-step-cue.js.
 */
export function protocolCues(config, defaults) {
    const cue = (key) => {
        const type = phoneType(config, key)
        return { enabled: type?.enabled !== false, title: text(type?.title), body: text(type?.body) }
    }
    const preAlert = phoneType(config, 'protocol.pre_alert')

    return {
        step: cue('protocol.step'),
        done: cue('protocol.done'),
        preAlert: {
            ...cue('protocol.pre_alert'),
            seconds: positive(preAlert?.params?.seconds, defaults.seconds),
            minGaitSeconds: positive(preAlert?.params?.min_gait_seconds, defaults.minGaitSeconds),
        },
    }
}
